import { Link } from "react-router-dom";
import { withLayout } from "../../components/feature/PageLayout";
import SEOHead from "../../components/feature/SEOHead";

const siteUrl = (import.meta.env.VITE_SITE_URL || "").replace(/\/+$/, "");

const pillars = [
  {
    title: "精油療癒知識",
    sub: "產品力",
    desc: "用生活化、實務化的芳香知識建立信任與專業。每週主題任務：情緒放鬆、睡眠儀式、專注力、居家香氛照護。",
    image: "https://readdy.ai/api/search-image?query=Beautiful%20arrangement%20of%20essential%20oil%20bottles%20with%20dried%20flowers%20and%20herbs%20on%20a%20warm%20wooden%20surface%2C%20soft%20natural%20lighting%2C%20elegant%20minimal%20composition%2C%20cream%20and%20amber%20tones%2C%20aromatherapy%20lifestyle%20photography%20with%20gentle%20shadows&width=700&height=500&seq=sys-pillar-01&orientation=landscape",
  },
  {
    title: "財富複製系統",
    sub: "商業力",
    desc: "心態調頻 → 產品體驗 → 分享話術 → 諮詢轉單 → 團隊複製。以標準化腳本與工具降低新手入門門檻。",
    image: "https://readdy.ai/api/search-image?query=Clean%20organized%20desk%20with%20a%20beautiful%20planner%20journal%20open%2C%20essential%20oil%20bottles%2C%20a%20laptop%20and%20coffee%20cup%2C%20warm%20natural%20light%2C%20feminine%20business%20planning%20aesthetic%2C%20cream%20and%20gold%20tones%2C%20productive%20peaceful%20workspace&width=700&height=500&seq=sys-pillar-02&orientation=landscape",
  },
  {
    title: "領導力培訓",
    sub: "心態力",
    desc: "以陪跑與回饋為核心：週會教練、KPI 追蹤、任務制挑戰，讓新人從跟著做，到能帶著做。",
    image: "https://readdy.ai/api/search-image?query=Warm%20intimate%20mentoring%20session%20between%20two%20women%2C%20one%20guiding%20the%20other%2C%20notebooks%20and%20essential%20oils%20on%20table%2C%20soft%20window%20light%2C%20supportive%20coaching%20atmosphere%2C%20cream%20and%20beige%20interior%2C%20feminine%20leadership%20photography&width=700&height=500&seq=sys-pillar-03&orientation=landscape",
  },
];

const phases = [
  {
    week: "第 1–2 週",
    title: "啟動 Onboarding",
    subtitle: "設定目標與體驗產品",
    points: [
      "建立個人定位與 90 天目標（收入 / 內容 / 名單數）",
      "完成「四大情境」產品體驗：睡眠、情緒、專注、居家",
      "加入 LINE 私密社群，熟悉工具與常見問答",
    ],
    image: "https://readdy.ai/api/search-image?query=Woman%20writing%20goals%20in%20a%20beautiful%20journal%20with%20essential%20oil%20bottles%20nearby%2C%20morning%20sunlight%20on%20a%20minimal%20desk%2C%20fresh%20start%20vibe%2C%20cream%20and%20warm%20beige%20tones%2C%20peaceful%20goal%20setting%20photography&width=600&height=450&seq=sys-phase-01&orientation=landscape",
  },
  {
    week: "第 3–4 週",
    title: "打底 Foundation",
    subtitle: "學習分享與建立名單",
    points: [
      "完成三支 30–60 秒短影音（個人故事 / 產品應用 / 金句）",
      "建立 30 人名單清單與邀約話術演練",
      "安排 1 場線上體驗會或小圈圈分享",
    ],
    image: "https://readdy.ai/api/search-image?query=Woman%20filming%20a%20video%20with%20smartphone%20on%20tripod%2C%20essential%20oil%20display%20in%20background%2C%20bright%20airy%20room%2C%20content%20creation%20setup%2C%20cream%20and%20beige%20interior%2C%20creative%20feminine%20workspace%20photography&width=600&height=450&seq=sys-phase-02&orientation=landscape",
  },
  {
    week: "第 5–8 週",
    title: "行動 Action",
    subtitle: "諮詢轉單與案例累積",
    points: [
      "每週 2–3 次諮詢，導入「需求 → 配方 → 跟進」流程",
      "累積 3–5 則使用回饋，製作案例貼文與限動",
      "參與團隊說明會，學會 10 分鐘商機簡報",
    ],
    image: "https://readdy.ai/api/search-image?query=Two%20women%20having%20a%20warm%20consultation%20over%20tea%2C%20essential%20oil%20bottles%20and%20notes%20on%20table%2C%20soft%20cafe%20lighting%2C%20professional%20yet%20friendly%20atmosphere%2C%20cream%20and%20amber%20tones%2C%20feminine%20business%20consulting&width=600&height=450&seq=sys-phase-03&orientation=landscape",
  },
  {
    week: "第 9–12 週",
    title: "成果 Milestone",
    subtitle: "完成首單與複製招募",
    points: [
      "完成第一筆成交（入門包 / 客製配方）",
      "邀請 1–2 位夥伴加入，啟動「帶一做一」",
      "產出個人 SOP 紀錄，準備升級為小組長",
    ],
    image: "https://readdy.ai/api/search-image?query=Celebrating%20woman%20with%20a%20small%20group%20of%20supportive%20friends%2C%20essential%20oil%20gift%20sets%20on%20table%2C%20warm%20golden%20lighting%2C%20achievement%20and%20joy%2C%20cream%20and%20warm%20amber%20tones%2C%20feminine%20success%20celebration%20photography&width=600&height=450&seq=sys-phase-04&orientation=landscape",
  },
];

function System() {
  return (
    <>
      <SEOHead
        structuredData={{
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "90 天芳香創富系統 SOP",
          "description": "以三大支柱（精油療癒知識、財富複製系統、領導力培訓）與四階段里程碑，協助新人建立行動節奏。",
          "step": [
            {
              "@type": "HowToStep",
              "name": "第 1-2 週｜啟動 Onboarding",
              "text": "建立個人定位與 90 天目標，完成四大情境產品體驗，加入 LINE 私密社群。"
            },
            {
              "@type": "HowToStep",
              "name": "第 3-4 週｜打底 Foundation",
              "text": "完成三支短影音，建立 30 人名單，安排線上體驗會。"
            },
            {
              "@type": "HowToStep",
              "name": "第 5-8 週｜行動 Action",
              "text": "每週 2-3 次諮詢，累積使用回饋，參與團隊說明會。"
            },
            {
              "@type": "HowToStep",
              "name": "第 9-12 週｜成果 Milestone",
              "text": "完成第一筆成交，邀請夥伴加入，產出個人 SOP 紀錄。"
            }
          ]
        }}
      />
      {/* Hero */}
      <section className="relative w-full h-[380px] md:h-[520px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20woman%20leading%20a%20business%20workshop%20with%20a%20large%20screen%20showing%20a%20flowchart%2C%20essential%20oil%20display%20on%20table%2C%20confident%20gesture%2C%20warm%20bright%20room%20with%20natural%20light%2C%20cream%20and%20beige%20interior%2C%20business%20training%20and%20empowerment%20photography&width=1800&height=900&seq=sys-hero-bg&orientation=landscape"
          alt="芳香創富系統 - 90天新人啟動SOP"
          title="香氛女王的幸福大地 芳香創富系統"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/30"></div>
        <div className="relative z-10 w-full max-w-[1120px] mx-auto px-4 md:px-6 text-center">
          <h1 className="text-[2rem] md:text-[2.75rem] font-bold text-white leading-[1.3] mb-3 md:mb-4">
            我們系統成功的核心：90 天內帶新人出單 SOP
          </h1>
          <p className="text-[1.1875rem] text-white/80 leading-[1.75] max-w-[720px] mx-auto mb-4">
            三大支柱 × 可複製流程 ×
            明確里程碑。每位新人都能在 90 天內建立行動節奏，完成第一次分享、第一次諮詢與第一筆成交，並學會從跟著做，到能帶著做。
          </p>
          <p className="text-[1rem] text-white/60 leading-[1.6] max-w-[680px] mx-auto">
            成果依個人投入、行動力與市場環境而不同，90 天為訓練節奏與目標規劃，不代表收入保證。
          </p>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 md:mb-12 gap-3">
            <h2 className="text-[1.5rem] md:text-[2.125rem] font-bold text-foreground-950 leading-[1.4]">
              三大支柱
            </h2>
            <p className="text-[1rem] text-foreground-600 max-w-[400px]">
              產品力 × 商業力 ×
              心態力，三個支柱撐起完整的創富系統
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7">
            {pillars.map((pillar) => (
              <div
                key={pillar.title}
                className="bg-white rounded-[18px] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)]"
              >
                <div className="w-full h-[200px] md:h-[240px] overflow-hidden">
                  <img
                    src={pillar.image}
                    alt={pillar.title}
                    title={pillar.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <span className="inline-block text-[1rem] font-medium text-primary-700 bg-primary-100/70 rounded-full px-2.5 py-0.5 mb-3">
                    {pillar.sub}
                  </span>
                  <h3 className="text-[1.25rem] md:text-[1.625rem] font-semibold text-foreground-950 leading-[1.5] mb-2.5">
                    {pillar.title}
                  </h3>
                  <p className="text-[1.1875rem] text-foreground-600 leading-[1.75]">
                    {pillar.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 90 Day SOP */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6">
          <div className="mb-10 md:mb-12 text-center">
            <h2 className="text-[1.5rem] md:text-[2.125rem] font-bold text-foreground-950 leading-[1.4] mb-2">
              90 天 SOP｜四階段里程碑
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {phases.map((phase) => (
              <div
                key={phase.week}
                className="bg-white rounded-[18px] overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] flex flex-col"
              >
                <div className="w-full h-[170px] md:h-[200px] overflow-hidden">
                  <img
                    src={phase.image}
                    alt={phase.title}
                    title={phase.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-4 md:p-5 flex-1 flex flex-col">
                  <span className="text-[1rem] font-medium text-primary-500 mb-2">
                    {phase.week}
                  </span>
                  <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-foreground-950 leading-[1.5] mb-1">
                    {phase.title}
                  </h3>
                  <p className="text-[1rem] text-foreground-600 leading-[1.6] mb-3">
                    {phase.subtitle}
                  </p>
                  <ul className="space-y-2 mt-auto">
                    {phase.points.map((point, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-[1rem] text-foreground-600 leading-[1.55]"
                      >
                        <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form + Side */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-[1120px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
            {/* Form */}
            <div className="lg:w-[60%]">
              <h2 className="text-[1.5rem] md:text-[2.125rem] font-bold text-foreground-950 leading-[1.4] mb-2">
                立即了解我的系統
              </h2>
              <p className="text-[1.1875rem] text-foreground-600 leading-[1.75] mb-6">
                填寫表單，我會寄出「新手 90 天啟動指南」與下一場說明會資訊。
              </p>
              <form
                data-readdy-form
                action="https://readdy.ai/api/form/d8pdsevij7vns5otj8qg"
                method="POST"
                className="space-y-4"
              >
                <div>
                  <label
                    htmlFor="sys-name"
                    className="block text-[1rem] font-medium text-foreground-950 mb-1.5"
                  >
                    姓名
                  </label>
                  <input
                    id="sys-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-[18px] border border-background-300 bg-white text-[1rem] text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="請輸入您的姓名"
                  />
                </div>
                <div>
                  <label
                    htmlFor="sys-email"
                    className="block text-[1rem] font-medium text-foreground-950 mb-1.5"
                  >
                    Email
                  </label>
                  <input
                    id="sys-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-[18px] border border-background-300 bg-white text-[1rem] text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="請輸入您的 Email"
                  />
                </div>
                <div>
                  <label
                    htmlFor="sys-line"
                    className="block text-[1rem] font-medium text-foreground-950 mb-1.5"
                  >
                    LINE ID / 連結
                  </label>
                  <input
                    id="sys-line"
                    name="line_id"
                    type="text"
                    className="w-full px-4 py-3 rounded-[18px] border border-background-300 bg-white text-[1rem] text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="請輸入您的 LINE ID"
                  />
                </div>
                <div>
                  <label
                    htmlFor="sys-topic"
                    className="block text-[1rem] font-medium text-foreground-950 mb-1.5"
                  >
                    想了解的主題
                  </label>
                  <select
                    id="sys-topic"
                    name="topic"
                    required
                    className="w-full px-4 py-3 rounded-[18px] border border-background-300 bg-white text-[1rem] text-foreground-950 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer appearance-none"
                  >
                    <option value="">請選擇</option>
                    <option value="90天出單SOP">90 天出單 SOP</option>
                    <option value="精油產品與應用">精油產品與應用</option>
                    <option value="彈性收入與商業模式">
                      彈性收入與商業模式
                    </option>
                    <option value="領導培訓/帶人複製">
                      領導培訓 / 帶人複製
                    </option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="sys-goal"
                    className="block text-[1rem] font-medium text-foreground-950 mb-1.5"
                  >
                    您的現況 / 目標{" "}
                    <span className="text-foreground-600 font-normal">
                      （選填）
                    </span>
                  </label>
                  <textarea
                    id="sys-goal"
                    name="goal"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-[18px] border border-background-300 bg-white text-[1rem] text-foreground-950 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                    placeholder="請簡單描述您的現況或目標"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center px-8 py-3.5 rounded-full text-[1.0625rem] font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200"
                >
                  送出表單
                </button>
              </form>
            </div>
            {/* Side */}
            <div className="lg:w-[40%]">
              <div className="bg-secondary-500 rounded-[18px] p-6 md:p-8 h-full flex flex-col justify-center">
                <p className="text-[1.1875rem] text-white/85 leading-[1.75] mb-5">
                  填寫表單，我會寄出「新手 90 天啟動指南」與下一場說明會資訊。
                </p>
                <a
                  href="https://line.me/R/ti/p/@your-id"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[1.0625rem] font-medium whitespace-nowrap cursor-pointer border-2 border-white/40 text-white hover:bg-white/10 transition-colors duration-200"
                >
                  加入 LINE@ <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default withLayout(System);